import * as React from "react";
import {
  Autocomplete,
  Button,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { Box, Grid, Stack } from "@mui/system";
import { VariableSizeList, ListChildComponentProps } from "react-window";
import AddIcon from "@mui/icons-material/Add";
import DownloadIcon from "@mui/icons-material/Download";
import AutoFixHighOutlinedIcon from "@mui/icons-material/AutoFixHighOutlined";
import LinearProgress, {
  linearProgressClasses,
} from "@mui/material/LinearProgress";
import { styled } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import TuneIcon from "@mui/icons-material/Tune";
import InsertLinkIcon from "@mui/icons-material/InsertLink";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import ChangeReceiptsStatus from "../ReceiptsModal/ChangeReceiptsStatusMoldal";
import DeleteReceiptModal from "../DeleteReceipts/DeleteReceiptModal";
import Link from "@mui/material/Link";
import AddReceiptsModal from "../AddReceipts/AddReceiptsModal";
import { useDispatch, useSelector } from "react-redux";
import {
  resetReceiptsForm,
  setLoadingModal,
  setManualReceiptsPayload,
  setSelectedReceiptNumber,
  setUploadReceiptsPayload,
} from "../../../../../features/financeReceiptSlice";
import { useSearchParams } from "react-router-dom";
import { useEffect } from "react";
import { ReceiptFilterItems } from "./ReceiptFilterItems";
import { ReceiptsEndPointLogic } from "../../../../../logic/Finance/ProviderExpense/ReceiptsEndpointLogic";
import {
  selectUserToken,
  setDisplayErrorSnackBar,
} from "../../../../../features/userSlice";
import store from "../../../../../store";
import dayjs from "dayjs";

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 10,
  borderRadius: 5,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: theme.palette.grey[200],
    ...theme.applyStyles("dark", {
      backgroundColor: theme.palette.grey[800],
    }),
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: "#43A047",
    ...theme.applyStyles("dark", {
      backgroundColor: "#43A047",
    }),
  },
}));

const ReceiptsTableHead = () => {
  const dispatch = useDispatch();
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const { selectedReceiptDateRange } = useSelector(
    (state: any) => state.receipts
  );
  const [openModal, setOpenModal] = React.useState(false);
  const [deleteReceiptsOpenModal, setDeleteReceiptsOpenModal] =
    React.useState(false);
  const [addReceiptsOpenModal, setAddReceiptsOpenModal] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const { receiptData, selectedReceiptNumber, selectedRow, pairingRate } =
    useSelector((state: any) => state.receipts);

  useEffect(() => {
    dispatch(setSelectedReceiptNumber(searchParams.get("search")));
    // eslint-disable-next-line
  }, []);

  const clearSelectedReceiptNumber = () => {
    dispatch(setSelectedReceiptNumber(null));
  };

  const handleOpenModal = () => {
    setOpenModal(true);
  };
  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleDeleteReceiptsOpenModal = () => {
    setDeleteReceiptsOpenModal(true);
  };
  const handleDeleteReceiptsCloseModal = () => {
    setDeleteReceiptsOpenModal(false);
  };

  const handleAddReceiptsOpenModal = async () => {
    setAddReceiptsOpenModal(true);
    try {
      dispatch(setLoadingModal(true));
      await Promise.all([
        ReceiptsEndPointLogic.getAllCompanies(oauthTokenHeader),
        ReceiptsEndPointLogic.getAllCurrencies(oauthTokenHeader),
      ]);
    } catch (err) {
      console.error("Failed to load manual input data", err);
      return;
    } finally {
      dispatch(setLoadingModal(false));
    }
  };
  const handleAddReceiptsCloseModal = () => {
    dispatch(setManualReceiptsPayload([]));
    dispatch(setUploadReceiptsPayload([]));
    dispatch(resetReceiptsForm());
    setAddReceiptsOpenModal(false);
  };

  const convertToCSV = (data: any) => {
    if (!Array.isArray(data) || data.length === 0) return "";

    // Flatten the data structure - one row per FME
    const flattenedData: any[] = [];

    data.forEach((receipt) => {
      const { fmes, ...receiptData } = receipt;

      if (Array.isArray(fmes) && fmes.length > 0) {
        fmes.forEach((fme) => {
          flattenedData.push({
            ...receiptData,
            ...fme,
          });
        });
      } else {
        // If no FMEs, include the receipt data anyway
        flattenedData.push(receiptData);
      }
    });

    if (flattenedData.length === 0) return "";

    // Define the column order explicitly
    const keys = [
      "receipt_id",
      "receipt_currency_id",
      "receipt_amount",
      "receipt_date",
      "receipt_currency_shared_uid",
      "company_shared_uid",
      "fme_id",
      "month",
      "currency",
      "status_id",
      "account_id",
      "cpa_income",
      "brand_names",
      "total_income",
      "revshare_income",
      "manual_currency_id",
      "finance_company_name",
      "invoice_total_income",
      "manual_invoice_total_income",
      "total_covered",
      "total_fmes_income",
      "total_policy",
      "total_manual",
    ];

    const header = keys.join(",");
    const rows = flattenedData.map((row) =>
      keys
        .map((k) => {
          const val =
            row[k] === null || row[k] === undefined ? "" : String(row[k]);
          // escape quotes and wrap in quotes if comma/newline present
          const escaped = val.replace(/"/g, '""');
          return /[",\n]/.test(escaped) ? `"${escaped}"` : escaped;
        })
        .join(",")
    );

    return [header, ...rows].join("\r\n");
  };

  const fetchAndDownload = async () => {
    const from_date = selectedReceiptDateRange[0]?.format("YYYY-MM-DD");
    const to_date = selectedReceiptDateRange[1]?.format("YYYY-MM-DD");
    try {
      setLoading(true);
      await ReceiptsEndPointLogic.getAllReceiptData(
        oauthTokenHeader,
        from_date,
        to_date
      );
      await new Promise((resolve) => setTimeout(resolve, 100));
      const timestamp = dayjs().format("MMM D, YYYY, h:mm A");
      const currentState = store.getState();
      const rawTableData = currentState.receipts.receiptData;
      let blob: Blob;
      let fileNameBase = `Receipts_List_From_${from_date}_To_${to_date}__at_${timestamp}`;
      const csv = Array.isArray(rawTableData)
        ? convertToCSV(rawTableData)
        : String(rawTableData);
      blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const filename = `${fileNameBase}.csv`;
      triggerBrowserDownload(blob, filename);
    } catch (error: any) {
      store.dispatch(
        setDisplayErrorSnackBar({
          message: error?.parsedText || "Something went wrong from the getdata",
          type: "error",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const triggerBrowserDownload = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  // Virtualized Listbox Component for better performance with large lists
  const LISTBOX_PADDING = 8; // px
  const ITEM_HEIGHT = 48; // px

  const OuterElementContext = React.createContext({});

  const OuterElementType = React.forwardRef<HTMLDivElement>((props, ref) => {
    const outerProps = React.useContext(OuterElementContext);
    return <div ref={ref} {...props} {...outerProps} />;
  });

  const ListboxComponent = React.forwardRef<
    HTMLDivElement,
    React.HTMLAttributes<HTMLElement>
  >(function ListboxComponent(props, ref) {
    const { children, ...other } = props;
    const itemData: React.ReactElement[] = [];
    (children as React.ReactElement[]).forEach(
      (item: React.ReactElement & { children?: React.ReactElement[] }) => {
        itemData.push(item);
        itemData.push(...(item.children || []));
      }
    );

    const itemCount = itemData.length;
    const itemSize = ITEM_HEIGHT;

    const getHeight = () => {
      if (itemCount > 8) {
        return 8 * itemSize;
      }
      return itemCount * itemSize;
    };

    const gridRef = React.useRef<any>(null);

    React.useEffect(() => {
      if (gridRef.current != null) {
        gridRef.current.resetAfterIndex(0, true);
      }
    }, [itemCount]);

    return (
      <div ref={ref}>
        <OuterElementContext.Provider value={other}>
          <VariableSizeList
            itemData={itemData}
            height={getHeight() + 2 * LISTBOX_PADDING}
            width="100%"
            ref={gridRef}
            outerElementType={OuterElementType}
            innerElementType="ul"
            itemSize={() => itemSize}
            overscanCount={5}
            itemCount={itemCount}
          >
            {({ data, index, style }: ListChildComponentProps) => {
              return React.cloneElement(data[index], {
                style: {
                  ...style,
                  top: (style.top as number) + LISTBOX_PADDING,
                },
              });
            }}
          </VariableSizeList>
        </OuterElementContext.Provider>
      </div>
    );
  });

  return (
    <>
      <Box sx={{ padding: " 16px " }}>
        <Grid container rowSpacing={2} columnSpacing={2} mb={2}>
          <Grid size={9}>
            <Typography variant="h3" sx={{ fontSize: "24px" }} mb={0.5}>
              Receipt List
            </Typography>
            <Button
              size="medium"
              color="secondary"
              data-tracker="527"
              //disabled
              sx={{ mr: 1 }}
              endIcon={<AddIcon />}
              onClick={handleAddReceiptsOpenModal}
            >
              Add New
            </Button>
            <AddReceiptsModal
              open={addReceiptsOpenModal}
              handleClose={handleAddReceiptsCloseModal}
            />
            <Button
              size="medium"
              color="secondary"
              data-tracker="528"
              sx={{ mr: 1 }}
              loading={loading}
              disabled={loading || receiptData?.length === 0}
              onClick={() => fetchAndDownload()}
              endIcon={<DownloadIcon />}
            >
              Download
            </Button>
            <Button
              disabled
              size="medium"
              color="secondary"
              sx={{ mr: 1 }}
              endIcon={<AutoFixHighOutlinedIcon />}
            >
              Verify Status
            </Button>
          </Grid>
          <Grid size={3} justifyContent={"flex-end"}>
            <Box
              border={1}
              borderColor={"rgba(0, 0, 0, 0.12)"}
              borderRadius={2}
              padding={1}
              maxWidth={300}
              justifyContent={"flex-end"}
              ml={"auto"}
            >
              <Box display={"flex"} justifyContent={"space-between"} mb={1}>
                <Typography>Pairing rate</Typography>
                <Typography color="rgba(0, 0, 0, 0.87)" fontSize={"12px"}>
                  <span style={{ fontSize: "20px", fontWeight: "500" }}>
                    {" "}
                    {Math.round(
                      pairingRate?.query_output?.[0]
                        ?.receipts_covered_percentage
                    ) || 0}
                    %
                  </span>{" "}
                  ({pairingRate?.query_output?.[0]?.receipts_covered || 0}/
                  {pairingRate?.query_output?.[0]?.total_receipts || 0}) paired
                </Typography>
              </Box>
              <Stack spacing={2} sx={{ flexGrow: 1 }}>
                <BorderLinearProgress
                  variant="determinate"
                  value={
                    pairingRate?.query_output?.[0]
                      ?.receipts_covered_percentage || 0
                  }
                />
              </Stack>
            </Box>
          </Grid>
        </Grid>

        <Grid container rowSpacing={2} columnSpacing={3} alignItems={"center"}>
          <Grid size={3}>
            <Autocomplete
              sx={{ width: "100%" }}
              disablePortal
              disableListWrap
              data-tracker="526"
              options={
                [
                  ...new Set(receiptData?.map((item: any) => item?.receipt_id)),
                ].filter(Boolean) || []
              }
              value={selectedReceiptNumber}
              ListboxComponent={ListboxComponent}
              onChange={(_, newValue) => {
                dispatch(setSelectedReceiptNumber(newValue));
                setSearchParams((prevParams) => {
                  const updatedParams = Object.fromEntries(prevParams); // Preserve previous params
                  if (newValue) {
                    updatedParams.search = newValue; // Set new value
                  } else {
                    delete updatedParams.search; // Remove from URL if cleared
                  }
                  return updatedParams;
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Receipt ID"
                  value={""}
                  data-tracker="526"
                  // sx={{ width: "300px" }}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ marginLeft: "12px" }} />
                      </InputAdornment>
                    ),
                  }}
                  color="secondary"
                />
              )}
              size="small"
            />
          </Grid>
          <Grid size={7}>
            <ReceiptFilterItems
              clearSelectedReceiptNumber={clearSelectedReceiptNumber}
            />
          </Grid>
          <Grid size={2}>
            <Box
              display={"flex"}
              alignItems={"center"}
              ml={"auto"}
              justifyContent={"flex-end"}
            >
              <Typography color="rgba(0, 0, 0, 0.87)" mr={1}>
                {" "}
                Bulk Actions:{" "}
              </Typography>
              <IconButton
                aria-label="fingerprint"
                disabled={selectedRow.length === 0}
                color="secondary"
                data-tracker="530"
                onClick={handleOpenModal}
              >
                <TuneIcon />
              </IconButton>
              <ChangeReceiptsStatus
                open={openModal}
                handleClose={handleCloseModal}
              />

              <Link href="/finance/receipts/receipts_pairing">
                <IconButton
                  disabled={selectedRow.length === 0}
                  aria-label="fingerprint"
                  data-tracker="529"
                  color="secondary"
                >
                  <InsertLinkIcon />
                </IconButton>
              </Link>

              <IconButton
                aria-label="fingerprint"
                disabled={selectedRow.length === 0}
                color="secondary"
                data-tracker="531"
                onClick={handleDeleteReceiptsOpenModal}
              >
                <DeleteOutlinedIcon />
              </IconButton>
              <DeleteReceiptModal
                open={deleteReceiptsOpenModal}
                handleClose={handleDeleteReceiptsCloseModal}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default ReceiptsTableHead;
